import { CommonModule, DatePipe } from '@angular/common';
import { Component, DestroyRef, inject, Inject, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { NotesDialogComponent } from '../notes-dialog/notes-dialog.component';
import { ReqEditorAccessDialogComponent } from '../req-editor-access-dialog/req-editor-access-dialog.component';
import { CONSTANTS } from '../../../../Constants/Constants';
import { CollaborationHistoryData, CollaborationHistoryEnum, CollaborationState, EditCollabRequest } from '../../../../models/dtos';
import { CollaborationApiService } from '../../../../services/api/collaboration-api.service';
import { OpenEstimateOperationsService } from '../../../../services/common/open-estimate-operations.service';
import { CommonDataService } from '../../../../services/common-data.service';
import { CommonFunctionsService } from '../../../../services/common-functions.service';
import { UserProfileService } from '../../../../services/common/user-profile.service';
import { EstimateSaveService } from '../../../../services/estimate-save.service';
import { CollaborationOperationsService } from '../../../../services/common/collaboration-operations.service';
import { DiscardCollabDialogComponent } from '../discard-collab-dialog/discard-collab-dialog.component';
import { PublishCollabDataDialogComponent } from '../publish-collab-data-dialog/publish-collab-data-dialog.component';
import { IPublishCollabDialogData } from '../../../../models/collaboration/IPublishCollabDialogData';
import { IPublishCollabResult } from '../../../../models/collaboration/IPublishCollabResult';
import { ICollabTableRow } from '../../../../models/collaboration/ICollabTableRow';
import { IOpenCollaborationHubDialogData } from '../../../../models/collaboration/IOpenCollaborationHubDialogData';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CollaboratorDetails } from '../../../../models/dtos/Classes/CollaboratorDetails';
import {MultilingualDatePipe} from "../../../../pipes/multilingual-date-pipe/multilingual-date-pipe";
import { MultilingualDateAdapter } from '../../../../adapters/multilingual-date.adapter';
import { DateAdapter, MAT_DATE_FORMATS, MAT_NATIVE_DATE_FORMATS } from '@angular/material/core';
import { GocCollaborationRestrictionHandlerService } from '../../../../services/common/goc-collaboration-restriction-handler.service';
import { EstimateCloseService } from '../../../../services/estimate-close.service';
import { NotificationMessageService } from '../../../../services/common/notification-message.service';

@Component({
  selector: 'app-open-collaboration-hub-dialog',
  imports: [
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    TranslatePipe,
    MatTableModule,
    CommonModule,
    MatDialogModule,
    MultilingualDatePipe
],
  templateUrl: './open-collaboration-hub-dialog.component.html',
  styleUrl: './open-collaboration-hub-dialog.component.scss',
  providers: [{ provide: DateAdapter, useClass: MultilingualDateAdapter }, { provide: MAT_DATE_FORMATS, useValue: MAT_NATIVE_DATE_FORMATS }, DatePipe]
})
export class OpenCollaborationHubDialogComponent implements OnInit {
    isChangeReqSent: boolean = false;
    changeRequestLogNotes: string = '';
    declineMessage = '';
    declineDisplayedColumns = ['decline-message', 'decline-notes'];
    isCurrentUserEditor = false;
    displayedColumns: string[] = ['collaborator', 'modifiedDate', 'history', 'notes'];
    pinnedDisplayedColumns: string[] = ['pinned-collaborator', 'pinned-modifiedDate', 'pinned-history', 'pinned-notes'];
    collabDataSource = new MatTableDataSource<ICollabTableRow>();
    pinnedRowDataSource = new MatTableDataSource<ICollabTableRow>();
    collaborationState: CollaborationState;
    CollaborationState = CollaborationState;
    currentCollaboratorData: CollaboratorDetails;
    collaboratorData: CollaboratorDetails[] = [];
    private destroyRef = inject(DestroyRef);
  
   constructor(public dialogRef: MatDialogRef<OpenCollaborationHubDialogComponent>, 
    private readonly collaborationApiService: CollaborationApiService,
    private readonly collaborationOperationsService: CollaborationOperationsService,
    private readonly userProfileService: UserProfileService,
    private readonly commonDataService: CommonDataService,
    private readonly commonFunctionsService: CommonFunctionsService,
    private readonly estimateSaveService: EstimateSaveService,
    private readonly openEstimateOperationsService: OpenEstimateOperationsService,
    private readonly gocCollaborationRestrictionHandlerService: GocCollaborationRestrictionHandlerService,
    private readonly translate: TranslateService,
    private readonly estimateCloseService: EstimateCloseService,
    private readonly notificationMessageDialog: NotificationMessageService,
    @Inject(MAT_DIALOG_DATA) public data: IOpenCollaborationHubDialogData,
    public dialog: MatDialog) {
    this.dialogRef.disableClose = true;
   }
   
  ngOnInit() {
    this.subscribeAccessRequestPending();
    this.subscribeAccessRequestDeclineMessage();
    this.subscribeCollaborationData();
  }

  private subscribeAccessRequestPending(): void {
    this.commonDataService.accessRequestPending$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(isPending => {
        this.isChangeReqSent = isPending;
      });
  }

  private subscribeAccessRequestDeclineMessage(): void {
    this.commonDataService.accessRequestDeclineMessage$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(message => {
        this.declineMessage = message;
      });
  }

  private subscribeCollaborationData(): void {
    this.commonDataService.collaborationData$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.getCollaborationHubData());
   }

   getCollaborationHubData() {

    this.collaborationState = this.commonDataService.getCollaborationState();
    this.collaboratorData = this.commonDataService.getCollaboratorData();
    const currentUserEmail = this.userProfileService.userInfo?.mail?.toLowerCase() ?? CONSTANTS.EMPTY_STRING;
    this.currentCollaboratorData = this.collaboratorData.find(
      collaborator => collaborator.email?.toLowerCase() === currentUserEmail
    ) ?? new CollaboratorDetails();
    const history = this.commonDataService.getCollaborationHistory();
    let editor: CollaborationHistoryData | undefined;
    const historyRows: ICollabTableRow[] = [];
    for (const item of history) {
      if (item.isCurrentEditor) {
        editor ??= item;
        continue;
      }
      historyRows.push({
        collaboratorName: item.collaboratorName,
        modifiedDate: item.modifiedDate ?? '',
        operation: item.historyId === null ? '' : this.collaborationOperationsService.getTranslatedCollaborationHistory(item.historyId),
        modificationNotes: item.modificationNotes
      });
    }
    historyRows.sort((first, second) => {
      const firstTimestamp = this.commonFunctionsService.collaborationTimestamp(first.modifiedDate);
      const secondTimestamp = this.commonFunctionsService.collaborationTimestamp(second.modifiedDate);
      if (!Number.isFinite(firstTimestamp)) {
        return Number.isFinite(secondTimestamp) ? 1 : 0;
      }
      if (!Number.isFinite(secondTimestamp)) {
        return -1;
      }
      return secondTimestamp - firstTimestamp;
    });
    this.isCurrentUserEditor = !!editor?.collaboratorEmail
      && editor.collaboratorEmail.toLowerCase() === currentUserEmail;
    this.pinnedRowDataSource.data = [{
      collaboratorName: editor?.collaboratorName ?? (this.collaborationState === CollaborationState.Open
        ? this.currentCollaboratorData.name ?? '' : ''),
      modifiedDate: editor?.modifiedDate ?? '',
      operation: '',
      modificationNotes: ''
    }];
    this.collabDataSource.data = historyRows;
   }


  get declineSummaryNotes(): string {
    return this.declineMessage.length > 30 ? `${this.declineMessage.slice(0, 30)}...` : this.declineMessage;
  }


  openNotesDialog(notes: string) {
    this.dialog.open(NotesDialogComponent, { 
      data: notes,
      disableClose: true,
      width: '500px',
      maxWidth: '90vw',
     });
  }

  closeElement() {
    this.dialogRef.close();
  }

  async publishChanges(): Promise<void> {
    const estimateSaveRequest = await this.estimateSaveService.createSaveEstimateRequest();
    const collaborators = this.commonDataService.getCollaboratorData();
    const data: IPublishCollabDialogData = { estimateSaveRequest, collaborators, closeOnFailure: true };
    const dialogRef = this.dialog.open(PublishCollabDataDialogComponent, {
      width: CONSTANTS.COLLABORATION_POPUP_WIDTH,
      disableClose: true,
      data
    });
    dialogRef.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(async (result: IPublishCollabResult | string | undefined) => {
      if (result === CONSTANTS.PUBLISH_FAILED) {
        await this.closeEstimateAfterCollabError();
        return;
      }
      if (typeof result === 'object' && result?.modificationNotes !== undefined) {
        const newRow: ICollabTableRow = {
          collaboratorName: this.collaboratorName(),
          modifiedDate: new Date().toISOString(),
          operation: this.collaborationOperationsService.getTranslatedCollaborationHistory(CollaborationHistoryEnum.Published),
          modificationNotes: result.modificationNotes
        };
        this.collabDataSource.data = [newRow, ...this.collabDataSource.data];
      }
    });
  }

  private collaboratorName(): string{
    return this.collaboratorData.find(collaborator => collaborator.email === this.userProfileService.userInfo?.mail)?.name ?? CONSTANTS.EMPTY_STRING;
  }

  discardCollabChanges(): void {
    const dialog = this.dialog.open(DiscardCollabDialogComponent, {
      width: CONSTANTS.COLLABORATION_POPUP_WIDTH,
      disableClose: true
    });
    dialog.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(async (result) => {
      if (result === CONSTANTS.DISCARD_FAILED) {
        await this.closeEstimateAfterCollabError();
        return;
      }
    });
  }

  // The API error itself is already shown by the global error interceptor.
  private async closeEstimateAfterCollabError(): Promise<void> {
    await this.estimateCloseService.closeEstimate();
    this.dialogRef.close();
  }

  requestEditorAccess(){
    this.dialog.open(ReqEditorAccessDialogComponent,{
      width: CONSTANTS.COLLABORATION_POPUP_WIDTH,
      disableClose: true
     });
    }

    editCollabEstimate(){
      if (this.commonDataService.isSapSubmitted) {
        this.notificationMessageDialog.showNotificationMessageDialog(
            CONSTANTS.TYPE_ERROR,
            'ERROR_TITLE',
            'COLLABORATION_HUB_EDIT_BEFORE_UNLOCK_ERROR',
            CONSTANTS.DEFAULT_DIALOG_WIDTH
        );
        return;
      }
      const request: EditCollabRequest = {
        openEstimateRequest:{
          userId: this.userProfileService.userInfo?.gid ?? CONSTANTS.EMPTY_STRING,
          estimateId: this.commonDataService.getEstimateId(),
          userEmailId: this.userProfileService.userInfo?.mail ?? CONSTANTS.EMPTY_STRING,
          culture: this.commonDataService.getCurrentApplicationCulture(),
          isArchive: this.commonDataService.isArchivedEstimate(), 
        },
        additionalInfo: this.translate.instant('CHECK_OUT_ESTIMATE'),
      };
      this.collaborationApiService.editCollabEstimate(request, true).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: async (response) => {
           await this.estimateCloseService.closeEstimate();
          await this.openEstimateOperationsService.openEstimate(response);
          this.dialogRef.close();
        },
        error: async (error) => {
          if (await this.gocCollaborationRestrictionHandlerService.isGocUserRestricted(error)) {
            return;
          }
        }
      }); 
    }
}
